import { useMutation } from "@apollo/client";
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { CallSchedule } from "../../../../types/api";
import {
  DeleteScheduleInputs,
  EditScheduleInputs,
  EditScheduleResponse,
} from "./types";
import { DELETE_CALL_SCHEDULE, UPDATE_CALL_SCHEDULE } from "../../queries";
import { useEffect, useState } from "react";
import { WebLinkButton } from "components/atoms/WebLinkButton";

interface EditScheduleProps {
  trigger: boolean;
  setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
  callSchedule: CallSchedule;
}

export const EditScheduleModal = (props: EditScheduleProps) => {
  const { handleSubmit, register, setValue } = useForm<EditScheduleInputs>({
    mode: "onChange",
  });
  const [showError, setShowError] = useState<boolean>(false);

  const navigate = useNavigate();

  const [editCallSchedule, { error }] = useMutation<
    EditScheduleResponse,
    EditScheduleInputs
  >(UPDATE_CALL_SCHEDULE);

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  // add dependency to update react-hook-form to db value if changes were discarded mid-edit
  useEffect(() => {
    setValue("cronExpression", props.callSchedule?.cronExpression ?? "");
  }, [props.callSchedule, props.trigger]);

  const onSubmit: SubmitHandler<EditScheduleInputs> = async (response) => {
    const id = props.callSchedule?.id;
    if (id) {
      try {
        await editCallSchedule({
          variables: {
            id: props.callSchedule?.id as number,
            cronExpression: response.cronExpression,
          },
        });
        navigate("/refresh");
      } catch (error) {
        console.error("Failed to update call schedule");
        setShowError(true);
      }
    }
  };

  const [deleteCallSchedule, { error: deleteError }] =
    useMutation<DeleteScheduleInputs>(DELETE_CALL_SCHEDULE);

  useEffect(() => {
    if (deleteError) {
      setShowError(true);
    }
  }, [deleteError]);

  const onDelete = async () => {
    const id = props.callSchedule?.id;
    if (id) {
      await deleteCallSchedule({
        variables: {
          id,
        },
      });
      navigate("/refresh");
    }
  };

  return (
    <>
      <Dialog open={props.trigger} onClose={() => props.setTrigger(false)}>
        <Grid
          container
          justifyContent="space-between"
          sx={{ padding: "0.5rem" }}
        >
          <DialogTitle>Edit Schedule</DialogTitle>
          <Button className="button" onClick={() => props.setTrigger(false)}>
            X
          </Button>
        </Grid>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
              container
              direction="row"
              alignItems="center"
              style={{ marginBottom: "40px", width: "100%" }}
            >
              <TextField
                {...register("cronExpression")}
                // margin="dense"
                label="CRON Expression"
                variant="standard"
                required={true}
                style={{ width: "inherit" }}
              />
            </Grid>
            <br />
            <Grid container justifyContent="space-between" gap={2}>
              <Button className="button" color="error" onClick={onDelete}>
                Delete Schedule
              </Button>
              <Button className="button" type="submit" variant="contained">
                Update Schedule
              </Button>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Failed to update call schedule.
        </Alert>
      </Snackbar>
    </>
  );
};
